import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageSeo from '@/components/base/PageSeo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { t } = useTranslation();
  const { comicSlug } = useParams<{ comicSlug: string }>();

  const comic = comics.find((c) => c.slug === comicSlug);
  const category = categories.find((c) => c.slug === comic?.category);

  if (!comic) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
        <h1 className="text-xl font-heading font-bold text-foreground-900 mt-4">{t('common.pageNotFound')}</h1>
        <p className="text-foreground-500 mt-2">{t('common.notFoundDesc')}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors"
        >
          <i className="ri-home-line"></i>
          {t('common.backToHome')}
        </Link>
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 5);

  const pageTitle = `${comic.title}_好看的韩漫漫画推荐_免费韩漫漫画全集_韩漫网`;
  const pageDesc = `韩漫网为您提供${comic.title}免费在线观看，${comic.author}作品，${comic.summary.substring(0, 80)}...点击立即阅读韩漫漫画全集。`;

  return (
    <>
      <PageSeo
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},${comic.tags.join(',')},好看的韩漫漫画推荐,免费韩漫漫画全集,韩漫网`}
        canonicalUrl={`https://hanmanwang.com/comic/${comic.slug}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": comic.title,
            "description": comic.summary,
            "author": {
              "@type": "Person",
              "name": comic.author
            },
            "datePublished": "2024-01-01",
            "dateModified": comic.updatedAt,
            "url": `https://hanmanwang.com/comic/${comic.slug}`,
            "image": comic.cover,
            "publisher": {
              "@type": "Organization",
              "name": "韩漫网"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://hanmanwang.com/"
              },
              ...(category
                ? [{
                    "@type": "ListItem",
                    "position": 2,
                    "name": `${category.name}韩漫`,
                    "item": `https://hanmanwang.com/category/${category.slug}`
                  } as const]
                : []),
              {
                "@type": "ListItem",
                "position": category ? 3 : 2,
                "name": comic.title,
                "item": `https://hanmanwang.com/comic/${comic.slug}`
              }
            ]
          }
        ]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col md:flex-row gap-8 mt-4">
          <div className="md:w-[280px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden border border-background-200/70 aspect-[3/4]">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover"
              />
            </div>
            <Link
              to={`/comic/${comic.slug}`}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              {t('comic.startReading')}
            </Link>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-foreground-400"></i>
                {t('comic.author')}：{comic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-flag-line text-foreground-400"></i>
                {t('comic.status')}：
                <span className={comic.status === 'ongoing' ? 'text-primary-500 font-medium' : 'text-secondary-500 font-medium'}>
                  {comic.status === 'ongoing' ? t('home.statusOngoing') : t('home.statusCompleted')}
                </span>
              </span>
              {category && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-folder-line text-foreground-400"></i>
                  {t('comic.category')}：
                  <Link to={`/category/${category.slug}`} className="text-primary-500 hover:text-primary-600">
                    {category.name}
                  </Link>
                </span>
              )}
            </div>

            <div className="flex items-center gap-6 mt-3 text-sm">
              <span className="flex items-center gap-1 text-accent-500 font-medium">
                <i className="ri-star-fill"></i>
                {comic.rating} {t('comic.rating')}
              </span>
              <span className="text-foreground-500">
                <i className="ri-eye-line mr-1 text-foreground-400"></i>
                {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} {t('comic.views')}
              </span>
              <span className="text-foreground-500">
                <i className="ri-time-line mr-1 text-foreground-400"></i>
                {t('comic.updatedAt')}：{comic.updatedAt}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6">
              <h2 className="text-base font-semibold text-foreground-900 mb-2">{t('comic.summary')}</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.summary}</p>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
            {t('comic.chapters')}
            <span className="text-sm font-normal text-foreground-500 ml-2">
              {t('comic.chapterCount', { count: comic.chapters.length })}
            </span>
          </h2>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {comic.chapters.slice(0, 50).map((chapter) => (
                <Link
                  key={chapter.number}
                  to={`/comic/${comic.slug}`}
                  className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors border border-background-200/70 hover:border-primary-200"
                >
                  <span className="line-clamp-1">{chapter.title}</span>
                </Link>
              ))}
            </div>
            {comic.chapters.length > 50 && (
              <div className="text-center mt-4">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="text-sm text-primary-500 hover:text-primary-600"
                >
                  {t('home.viewAll')}（共 {comic.chapters.length} 话）
                </Link>
              </div>
            )}
          </div>
        </div>

        {relatedComics.length > 0 && (
          <div className="mt-14">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">{t('comic.relatedTitle')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.slug}`}
                  className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={rc.cover}
                      alt={rc.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        rc.status === 'ongoing' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                      }`}>
                        {rc.status === 'ongoing' ? t('home.statusOngoing') : t('home.statusCompleted')}
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}